import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button, confirm, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, TrashIcon } from "lucide-react";
import {
  useCancelPrivateLessonBooking,
  usePrivateLessonBookingQuery,
  usePrivateLessonPackageQuery,
  useUpdatePrivateLessonBooking,
} from "../api/queries";
import { BookingEditForm } from "../ui/BookingEditForm";

export function PrivateLessonBookingEditPage() {
  const nav = useNavigate({
    from: "/app/private-lessons/bookings/$id/edit",
  });
  const { id } = useParams({
    from: "/app/private-lessons/bookings/$id/edit",
  });
  const { data: booking, ...result } = usePrivateLessonBookingQuery(id);
  const { data: packageData } = usePrivateLessonPackageQuery(
    booking?.packageId ?? "",
  );
  const updateBooking = useUpdatePrivateLessonBooking();
  const cancelBooking = useCancelPrivateLessonBooking();
  const lockedOffSite =
    packageData?.templateDefaultLocationKind === "off_site";

  if (!booking) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "ການຈອງຮຽນສ່ວນຕົວ", to: "/app/private-lessons/bookings" },
              { label: "ແກ້ໄຂ" },
            ]}
          />
        </Header>
        <Main>
          <div className="flex flex-col items-center justify-center py-12">
            <p className="mb-4 text-muted-foreground">ກຳລັງໂຫຼດການຈອງ</p>
          </div>
        </Main>
      </>
    );
  }

  // Check if booking can be edited
  if (booking.status === "completed" || booking.status === "cancelled") {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "ການຈອງຮຽນສ່ວນຕົວ", to: "/app/private-lessons/bookings" },
              { label: "ແກ້ໄຂ" },
            ]}
          />
        </Header>
        <Main>
          <div className="flex flex-col items-center justify-center py-12">
            <p className="mb-4 text-muted-foreground">
              ບໍ່ສາມາດແກ້ໄຂການຈອງທີ່ສຳເລັດຫຼືຍົກເລີກແລ້ວ
            </p>
            <Button
              variant="outline"
              onClick={() =>
                nav({
                  to: "/app/private-lessons/bookings/$id",
                  params: { id },
                })
              }
            >
              ກັບຄືນ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການຈອງຮຽນສ່ວນຕົວ", to: "/app/private-lessons/bookings" },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂການຈອງຮຽນສ່ວນຕົວ
            </h2>
            <p className="text-muted-foreground">ແກ້ໄຂຂໍ້ມູນການຈອງຮຽນສ່ວນຕົວ</p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() =>
                nav({
                  to: "/app/private-lessons/bookings/$id",
                  params: { id },
                })
              }
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                const ok = await confirm({
                  title: "ຍົກເລີກການຈອງ",
                  description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກການຈອງນີ້?",
                  actionText: "ຍົກເລີກ",
                  ActionProps: {
                    variant: "destructive",
                  },
                });
                if (ok) {
                  toast.promise(cancelBooking.mutateAsync(id), {
                    loading: "ກຳລັງຍົກເລີກ...",
                    success: "ຍົກເລີກການຈອງສຳເລັດ",
                    error: "ຍົກເລີກການຈອງລົ້ມເຫຼວ",
                  });
                  nav({
                    to: "/app/private-lessons/bookings/$id",
                    params: { id },
                  });
                }
              }}
            >
              <TrashIcon className="size-4" />
              ຍົກເລີກການຈອງ
            </Button>
          </div>
        </div>

        <div className="mt-6">
          <BookingEditForm
            initialValues={booking}
            lockedOffSite={lockedOffSite}
            onSubmit={async (vals) => {
              try {
                await updateBooking.mutateAsync({ id, input: vals });
                nav({
                  to: "/app/private-lessons/bookings/$id",
                  params: { id },
                });
              } catch {
                // Error handling is done by the mutation
              }
            }}
            submitting={updateBooking.isPending}
          />
        </div>
      </Main>
    </>
  );
}
